import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Container, Spinner } from 'react-bootstrap';
import ErrorBoundary from '../ErrorBoundary';
import FieldModeCompletionForm from './FieldModeCompletionForm';
import FieldModePreFlightForm from './FieldModePreFlightForm';
import { SpraySession } from '../../services/sprayService';
import { getSessionWithCacheFallback } from '../../services/spraySessionCacheService';

const OBJECT_ID_PATTERN = /^[0-9a-fA-F]{24}$/;

/**
 * Fetches the session and routes to either pre-flight or completion form
 * based on session.startDate. Lives inside the ErrorBoundary so a throw
 * during fetch (e.g. a synchronous service mock failure in tests, or a
 * real render-time exception) is caught by the boundary instead of
 * crashing the surrounding Spray module.
 */
const FieldModeFormRouter: React.FC<{ sessionId: string }> = ({ sessionId }) => {
  const { t } = useTranslation();
  const [session, setSession] = useState<SpraySession | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError('');
    // Calling sprayService.getSession synchronously here (rather than in a
    // separate effect) keeps a synchronous throw on the render path so the
    // ErrorBoundary can catch it.
    let pending: Promise<{ session: SpraySession }> | null = null;
    try {
      // Cache-aware fetch: online → /sessions/:id; offline / transient
      // network error → local Dexie cache. Without this, an offline
      // operator who tapped a cached card would land on a "Network
      // Error" alert despite the session being available locally.
      pending = getSessionWithCacheFallback(sessionId);
    } catch (err: any) {
      // Propagate to the boundary.
      throw err;
    }
    pending
      .then(({ session: loaded }) => {
        if (cancelled) return;
        setSession(loaded);
      })
      .catch((err: any) => {
        if (cancelled) return;
        setLoadError(err?.message || 'Failed to load session.');
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  if (loading) {
    return (
      <div className="text-center p-4">
        <Spinner animation="border" role="status" />
        <div className="mt-2">{t('common.loading')}</div>
      </div>
    );
  }
  if (loadError) {
    return <Alert variant="danger">{loadError}</Alert>;
  }
  if (!session) return null;
  // Track: spray_weather_enhancements_20260429 / Phase 3.
  // The pre-flight form fires onStarted as soon as Start Session
  // succeeds. Updating the local `session` state here flips the
  // ternary to FieldModeCompletionForm on the next render, so the
  // operator transitions straight to the close sign-off — no
  // intermediate "already started → Complete Session" CTA.
  return session.startDate
    ? <FieldModeCompletionForm sessionId={sessionId} />
    : (
      <FieldModePreFlightForm
        sessionId={sessionId}
        onStarted={updated => setSession(updated)}
      />
    );
};

/**
 * Route-handler landing page mounted at /spray/sessions/:id/complete.
 *
 * Two entry points funnel into this component:
 *   1. The operator scans the QR on a printed Spray Instruction — the
 *      phone's native camera app opens the encoded URL in the browser,
 *      which resolves to this route.
 *   2. The operator taps a row in OperatorSessionList — a React Router
 *      <Link> navigates here.
 *
 * Validates the :id URL param against a 24-hex ObjectId pattern before
 * mounting a form, so malformed QR payloads produce a recoverable error
 * page rather than a cryptic fetch failure downstream.
 *
 * Routes between two forms based on the session's lifecycle state:
 *   - session.startDate == null  → FieldModePreFlightForm (PPE + start)
 *   - session.startDate != null  → FieldModeCompletionForm (sign-off)
 *
 * Track: operator_completion_20260412 / Phases 3 + 4.
 *        spray_session_workflow_20260427 / Phase 3 / Task 3.
 */
const SpraySessionCompleteLanding: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const idIsValid = !!id && OBJECT_ID_PATTERN.test(id);

  return (
    <Container className="spray-session-complete-landing p-3">
      <h2>{t('spray.qrScanner.title')}</h2>

      {!idIsValid && (
        <>
          <Alert variant="danger" className="mt-3">
            {t('spray.qrScanner.invalidLink')}
          </Alert>
          <Link to="/protection/my-sessions" className="btn btn-primary">
            {t('spray.operatorSessions.title')}
          </Link>
        </>
      )}

      {idIsValid && (
        <>
          <p className="text-muted mt-2">{t('spray.qrScanner.instructions')}</p>
          <div className="text-muted small mb-3" data-testid="session-id-display">
            {id}
          </div>
          {/* Dedicated ErrorBoundary around the sign-off form so a render
              crash inside the form (or the FieldModeFormRouter's session
              fetch) does not take out the rest of the Spray module.
              Sentry.captureException is called inside the boundary's
              componentDidCatch — no explicit wire-up needed here. */}
          <ErrorBoundary moduleName="Field Mode Sign-Off">
            <FieldModeFormRouter sessionId={id!} />
          </ErrorBoundary>
          <Link to="/protection/my-sessions" className="btn btn-link mt-3 px-0">
            ← {t('spray.operatorSessions.title')}
          </Link>
        </>
      )}
    </Container>
  );
};

export default SpraySessionCompleteLanding;
